import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "react-oidc-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// The theme and the faces fowlguard.com shares with the console, one copy each (branding/theme,
// branding/fonts), imported at build time before the console names them in globals.css.
import "../../../../branding/fonts/fonts.css";
import "../../../../branding/theme/tokens.css";
import "./styles/globals.css";
import { applyTheme } from "./lib/theme";

// Before anything renders, so the first paint is already in the chosen theme rather than flashing
// the other one - including the sign-in callback and the fatal-error screen below.
applyTheme();

/**
 * Puts a readable message on screen instead of leaving a blank page.
 *
 * The modules that read `import.meta.env` throw at import time when a required variable is
 * missing. That is the right behavior - a portal that cannot reach its API should not pretend to
 * load - but an uncaught throw during module evaluation leaves an empty `<div id="root">` and the
 * only explanation in a console nobody has open. This has already happened once, for a genuinely
 * unobvious reason: `vite build` runs in production mode and never reads `.env.development`, so a
 * build whose values lived only there shipped with no configuration at all.
 */
function renderFatal(message: string) {
    const root = document.getElementById("root");
    if (!root) {
        return;
    }
    const pre = document.createElement("pre");
    pre.style.cssText =
        "margin:0;padding:2rem;font:13px/1.6 ui-monospace,Consolas,monospace;white-space:pre-wrap";
    pre.textContent = `Fowlguard could not start.\n\n${message}`;
    root.replaceChildren(pre);
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false
        }
    }
});

const rootEl = document.getElementById("root");
if (!rootEl) {
    throw new Error("Root element #root not found in index.html");
}

// Both imported here rather than at the top so a configuration error is caught and shown. Static
// imports are hoisted above this code and would throw before anything could render the reason -
// and App pulls in the API client, which throws on its own missing variable, so importing only
// authConfig lazily would still leave the blank page for half the cases.
void (async () => {
    try {
        const [{ oidcConfig }, { App }] = await Promise.all([
            import("@/auth/authConfig"),
            import("./App"),
        ]);
        ReactDOM.createRoot(rootEl).render(
            <React.StrictMode>
                <AuthProvider {...oidcConfig}>
                    <QueryClientProvider client={queryClient}>
                        <BrowserRouter>
                            <App />
                        </BrowserRouter>
                    </QueryClientProvider>
                </AuthProvider>
            </React.StrictMode>
        );
    } catch (e) {
        renderFatal(e instanceof Error ? e.message : String(e));
    }
})();
